import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import { ReactComponent as DeleteIcon } from "assets/icons/deleteIcon.svg";
import * as yup from "yup";
import sanitizeHtml from "sanitize-html";
import API from "helpers/api";
import ColourConstants from "helpers/colourConstants";
import { isoToLocalInput, localInputToIso } from "helpers/date";
import { sanitizedConfig } from "helpers/utils";
import { buildPreviewUpdate } from "helpers/releaseNotePreview";
import TextEditor from "components/Elements/TextEditor";
import WhatsNewDialog from "components/ProductUpdates/WhatsNewDialog";
import { useDispatch } from "react-redux";
import { showError, showSuccess } from "../../redux/common/actions";

// Optional URL fields: empty is allowed, otherwise must be a valid URL.
const URL_SCHEMA = yup.string().url();

const AUDIENCES = [
	{ value: "all", label: "Web & Mobile" },
	{ value: "web", label: "Web only" },
	{ value: "mobile", label: "Mobile only" },
];
const IMPORTANCES = [
	{ value: "normal", label: "Normal (sidebar only)" },
	{ value: "featured", label: "Featured (first-login modal)" },
];

const EMPTY_FORM = {
	id: null,
	title: "",
	version: "",
	shortDescription: "",
	imageUrl: "",
	imageKey: "",
	externalUrl: "",
	audience: "all",
	importance: "featured",
	publishedAtLocal: "",
};

const isInvalidUrl = (value) =>
	Boolean(value) && !URL_SCHEMA.isValidSync(value);

// The rich-text value is an HTML string in form state (not a ReactQuill ref), so
// check emptiness by stripping tags/whitespace - ReactQuill's empty doc is "<p><br></p>".
const isHtmlEmpty = (html) =>
	!html ||
	html
		.replace(/<[^>]*>/g, "")
		.replace(/&nbsp;/g, " ")
		.trim().length === 0;

function ReleaseNotesAdminPage() {
	const dispatch = useDispatch();
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [form, setForm] = useState(null);
	const [saving, setSaving] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [uploadedImagePreview, setUploadedImagePreview] = useState("");
	const [confirmDelete, setConfirmDelete] = useState(null);
	const [previewOpen, setPreviewOpen] = useState(false);

	const handleImageFile = async (e) => {
		const file = e.target.files?.[0];
		e.target.value = ""; // let the same file be re-picked after a remove
		if (!file) return;

		const data = new FormData();
		data.append("file", file);
		setUploading(true);
		try {
			const res = await API.post("/api/product-updates/uploadImage", data);
			const imageKey = res?.data?.imageKey;
			if (!imageKey) throw new Error("no key");
			setForm((prev) => (prev ? { ...prev, imageKey } : prev));
			setUploadedImagePreview(URL.createObjectURL(file));
		} catch {
			dispatch(showError("Could not upload the image."));
		} finally {
			setUploading(false);
		}
	};

	const removeImage = () => {
		setForm((prev) => (prev ? { ...prev, imageKey: "" } : prev));
		setUploadedImagePreview("");
	};

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const res = await API.get(
				"/api/product-updates/admin?page=1&pageSize=100",
			);
			setItems(res?.data?.items || []);
		} catch {
			setItems([]);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const openCreate = () => {
		setUploadedImagePreview("");
		setForm({
			...EMPTY_FORM,
			publishedAtLocal: isoToLocalInput(new Date().toISOString()),
		});
	};

	const openEdit = (item) => {
		setUploadedImagePreview(item.imageKey ? item.displayImageUrl : "");
		setForm({
			id: item.id,
			title: item.title || "",
			version: item.version || "",
			shortDescription: item.shortDescription || "",
			imageUrl: item.imageUrl || "",
			imageKey: item.imageKey || "",
			externalUrl: item.externalUrl || "",
			audience: item.audience || "all",
			importance: item.importance || "normal",
			publishedAtLocal: isoToLocalInput(item.publishedAt),
		});
	};

	const setField = (key) => (e) =>
		setForm((prev) => (prev ? { ...prev, [key]: e.target.value } : prev));

	const handleSave = async () => {
		if (!form) return;
		if (!form.title.trim() || isHtmlEmpty(form.shortDescription)) {
			dispatch(showError("Title and description are required."));
			return;
		}

		if (isInvalidUrl(form.imageUrl) || isInvalidUrl(form.externalUrl)) {
			dispatch(showError("Image and article URLs must be valid links."));
			return;
		}

		const payload = {
			title: form.title.trim(),
			version: form.version.trim() || null,
			shortDescription: sanitizeHtml(form.shortDescription, sanitizedConfig),
			imageUrl: form.imageUrl.trim() || null,
			imageKey: form.imageKey || null,
			externalUrl: form.externalUrl.trim() || null,
			audience: form.audience,
			importance: form.importance,
			// Cleared publish field means "publish now" rather than silently dropping it.
			publishedAt:
				localInputToIso(form.publishedAtLocal) || new Date().toISOString(),
		};

		setSaving(true);
		try {
			if (form.id) {
				await API.put(`/api/product-updates/${form.id}`, payload);
			} else {
				await API.post("/api/product-updates", payload);
			}
			dispatch(
				showSuccess(
					form.id ? "Release note updated." : "Release note created.",
				),
			);
			setForm(null);
			load();
		} catch {
			dispatch(showError("Could not save the release note."));
		} finally {
			setSaving(false);
		}
	};

	const closePreview = () => setPreviewOpen(false);

	const openPreviewArticle = () => {
		if (form?.externalUrl) {
			window.open(form.externalUrl, "_blank", "noopener,noreferrer");
		}
	};

	const handleDelete = async (item) => {
		setConfirmDelete(null);
		try {
			await API.delete(`/api/product-updates/${item.id}`);
			dispatch(showSuccess("Release note deleted."));
			load();
		} catch {
			dispatch(showError("Could not delete the release note."));
		}
	};

	return (
		<Box sx={{ p: 1 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					mb: 2,
				}}
			>
				<Box
					sx={{
						font: "700 22px 'Roboto Condensed', sans-serif",
						color: "#393939",
					}}
				>
					Release notes
				</Box>
				<Button
					variant="contained"
					onClick={openCreate}
					sx={{ backgroundColor: "#307AD7" }}
				>
					Add release note
				</Button>
			</Box>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
					<CircularProgress />
				</Box>
			) : (
				<Box sx={{ border: "1px solid #C5C5C8", backgroundColor: "#fff" }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Title</TableCell>
								<TableCell>Version</TableCell>
								<TableCell>Audience</TableCell>
								<TableCell>Importance</TableCell>
								<TableCell>Published</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBodyRows
							items={items}
							onEdit={openEdit}
							onDelete={setConfirmDelete}
						/>
					</Table>
				</Box>
			)}

			<Dialog
				open={Boolean(form)}
				onClose={() => setForm(null)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					{form?.id ? "Edit release note" : "Add release note"}
				</DialogTitle>
				<DialogContent>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
						<TextField
							label="Title"
							value={form?.title || ""}
							onChange={setField("title")}
							required
							fullWidth
						/>
						<TextField
							label="Version"
							value={form?.version || ""}
							onChange={setField("version")}
							placeholder="v3.41"
							fullWidth
						/>
						<Box>
							<Box
								sx={{
									font: "400 12px Roboto, sans-serif",
									color: "#545353",
									mb: 0.5,
								}}
							>
								Short description *
							</Box>
							<TextEditor
								placeholder="What changed…"
								value={form?.shortDescription || ""}
								onChange={(content) =>
									setForm((prev) =>
										prev ? { ...prev, shortDescription: content } : prev,
									)
								}
							/>
						</Box>
						<TextField
							label="Image URL"
							value={form?.imageUrl || ""}
							onChange={setField("imageUrl")}
							placeholder="https://…"
							error={isInvalidUrl(form?.imageUrl)}
							helperText={
								isInvalidUrl(form?.imageUrl)
									? "Enter a valid URL."
									: "Optional. When set, the update uses the hero layout."
							}
							fullWidth
						/>
						<Box>
							<Box
								sx={{
									font: "400 12px Roboto, sans-serif",
									color: "#545353",
									mb: 0.75,
								}}
							>
								Or upload an image (screenshots stay crisp - the original
								PNG/JPG is kept, not re-compressed). An uploaded image is used
								ahead of the URL above.
							</Box>
							{uploadedImagePreview ? (
								<Box
									component="img"
									src={uploadedImagePreview}
									alt="Release note image preview"
									sx={{
										display: "block",
										maxHeight: 140,
										maxWidth: "100%",
										objectFit: "contain",
										border: "1px solid #C5C5C8",
										borderRadius: 1,
										backgroundColor: "#F2F2F2",
										mb: 1,
									}}
								/>
							) : null}
							<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
								<Button
									component="label"
									variant="outlined"
									disabled={uploading}
								>
									{uploading
										? "Uploading…"
										: uploadedImagePreview
											? "Replace image"
											: "Upload image"}
									<input
										hidden
										type="file"
										accept="image/*"
										onChange={handleImageFile}
									/>
								</Button>
								{form?.imageKey ? (
									<Button
										color="error"
										onClick={removeImage}
										disabled={uploading}
									>
										Remove
									</Button>
								) : null}
							</Box>
						</Box>
						<TextField
							label="Freshdesk article URL"
							value={form?.externalUrl || ""}
							onChange={setField("externalUrl")}
							placeholder="https://emi.freshdesk.com/…"
							error={isInvalidUrl(form?.externalUrl)}
							helperText={
								isInvalidUrl(form?.externalUrl)
									? "Enter a valid URL."
									: undefined
							}
							fullWidth
						/>
						<TextField
							label="Audience"
							value={form?.audience || "all"}
							onChange={setField("audience")}
							select
							fullWidth
						>
							{AUDIENCES.map((a) => (
								<MenuItem key={a.value} value={a.value}>
									{a.label}
								</MenuItem>
							))}
						</TextField>
						<TextField
							label="Importance"
							value={form?.importance || "normal"}
							onChange={setField("importance")}
							select
							fullWidth
						>
							{IMPORTANCES.map((i) => (
								<MenuItem key={i.value} value={i.value}>
									{i.label}
								</MenuItem>
							))}
						</TextField>
						<TextField
							label="Publish at"
							type="datetime-local"
							value={form?.publishedAtLocal || ""}
							onChange={setField("publishedAtLocal")}
							InputLabelProps={{ shrink: true }}
							helperText="Can be in the future - it won't appear in-app until then."
							fullWidth
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setForm(null)} disabled={saving}>
						Cancel
					</Button>
					<Button
						onClick={() => setPreviewOpen(true)}
						variant="outlined"
						disabled={saving || uploading}
					>
						Preview
					</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						disabled={saving || uploading}
						sx={{ backgroundColor: "#23BB79" }}
					>
						{saving ? "Saving…" : "Save"}
					</Button>
				</DialogActions>
			</Dialog>

			{form && previewOpen ? (
				<WhatsNewDialog
					open
					updates={[buildPreviewUpdate(form, uploadedImagePreview)]}
					index={0}
					onNext={closePreview}
					onSkipAll={closePreview}
					onDismiss={closePreview}
					onReadArticle={openPreviewArticle}
				/>
			) : null}

			<Dialog
				open={Boolean(confirmDelete)}
				onClose={() => setConfirmDelete(null)}
				maxWidth="xs"
			>
				<DialogTitle>Delete release note?</DialogTitle>
				<DialogContent>
					<Box sx={{ pt: 0.5 }}>
						You are about to delete <strong>{confirmDelete?.title}</strong>.
						This cannot be undone.
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
					<Button
						onClick={() => handleDelete(confirmDelete)}
						variant="contained"
						sx={{ backgroundColor: ColourConstants.deleteButton }}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

function TableBodyRows({ items, onEdit, onDelete }) {
	if (items.length === 0) {
		return (
			<TableBody>
				<TableRow>
					<TableCell colSpan={6} sx={{ color: "#545353" }}>
						No release notes yet.
					</TableCell>
				</TableRow>
			</TableBody>
		);
	}

	return (
		<TableBody>
			{items.map((item) => (
				<TableRow key={item.id} hover>
					<TableCell>{item.title}</TableCell>
					<TableCell>{item.version || "-"}</TableCell>
					<TableCell sx={{ textTransform: "capitalize" }}>
						{item.audience}
					</TableCell>
					<TableCell sx={{ textTransform: "capitalize" }}>
						{item.importance}
					</TableCell>
					<TableCell>
						{item.publishedAt
							? new Date(item.publishedAt).toLocaleString()
							: "-"}
					</TableCell>
					<TableCell align="right">
						<Box
							sx={{
								display: "flex",
								gap: 2,
								alignItems: "center",
								justifyContent: "flex-end",
							}}
						>
							<Button size="small" onClick={() => onEdit(item)}>
								Edit
							</Button>
							<DeleteIcon
								onClick={() => onDelete(item)}
								style={{
									transform: "scale(0.7)",
									color: ColourConstants.deleteButton,
									cursor: "pointer",
								}}
							/>
						</Box>
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);
}

export default ReleaseNotesAdminPage;
